/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { defineMessages, injectIntl } from 'react-intl'

import { EuiFlexGroup, EuiFlexItem, EuiFormLabel, EuiRadio, EuiSpacer, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { Scripting, ScriptingValue } from '@modules/ui-types'

import { getTooltip } from './utility'

import type { WrappedComponentProps } from 'react-intl'

const messages = defineMessages({
  disableScripts: {
    id: `deployment-configure-scripts.disable-stored-scripts`,
    defaultMessage: `Disable stored scripts`,
  },
  enableSandboxedScripts: {
    id: `deployment-configure-scripts.enable-sandboxed-stored-scripts`,
    defaultMessage: `Enable sandboxed stored scripts`,
  },
  enableScripts: {
    id: `deployment-configure-scripts.enable-stored-scripts`,
    defaultMessage: `Enable stored scripts`,
  },
})

type Props = WrappedComponentProps & {
  onUpdate: undefined | ((type: 'inline' | 'stored' | 'file', value: ScriptingValue) => void)
  value: Scripting['stored']
  lastValue: Scripting['stored']
}

const StoredScripts: React.FunctionComponent<Props> = ({
  intl: { formatMessage },
  onUpdate,
  value,
  lastValue,
}) => {
  const setValue = (_value: ScriptingValue) => onUpdate && onUpdate(`stored`, _value)

  return (
    <Fragment>
      <EuiFormLabel>
        <FormattedMessage
          id='deployment-configure-scripts.stored-scripts'
          defaultMessage='Stored Scripts'
        />
      </EuiFormLabel>
      <EuiSpacer size='s' />
      <EuiText size='s'>
        <FormattedMessage
          id='deployment-configure-scripts.stored-scripts-description'
          defaultMessage='Scripts can be {stored} and referenced by their name. This is a safer option than inlining, as you can control allowed scripts by limiting access to the .scripts index with Security.'
          values={{
            stored: (
              <em>
                <FormattedMessage
                  id='deployment-configure-scripts.stored-scripts-description-emphasis'
                  defaultMessage='stored'
                />
              </em>
            ),
          }}
        />
      </EuiText>
      <EuiSpacer size='s' />

      <EuiFlexGroup gutterSize='s'>
        <EuiFlexItem grow={false}>
          <EuiRadio
            id='stored-scripts-off'
            name='stored-scripts'
            onChange={() => setValue(`off`)}
            checked={value === `off`}
            label={formatMessage(messages.disableScripts)}
            disabled={!onUpdate}
          />
        </EuiFlexItem>

        {getTooltip(`off`, value, lastValue)}
      </EuiFlexGroup>

      <EuiFlexGroup gutterSize='s'>
        <EuiFlexItem grow={false}>
          <EuiRadio
            id='stored-scripts-sandbox'
            name='stored-scripts'
            onChange={() => setValue(`sandbox`)}
            checked={value === `sandbox`}
            label={formatMessage(messages.enableSandboxedScripts)}
            disabled={!onUpdate}
          />
        </EuiFlexItem>

        {getTooltip(`sandbox`, value, lastValue)}
      </EuiFlexGroup>

      <EuiFlexGroup gutterSize='s'>
        <EuiFlexItem grow={false}>
          <EuiRadio
            id='stored-scripts-on'
            name='stored-scripts'
            onChange={() => setValue(`on`)}
            checked={value === `on`}
            label={formatMessage(messages.enableScripts)}
            disabled={!onUpdate}
          />
        </EuiFlexItem>

        {getTooltip(`on`, value, lastValue)}
      </EuiFlexGroup>
    </Fragment>
  )
}

export default injectIntl(StoredScripts)
